import { type ReactNode } from 'react';
import type { Creator } from '@/types';
import { useAuth } from '@/hooks/use-auth';
import { formatFollowers } from '@/utils/formatters';
import { useCreatorFilters } from '../hooks/use-creator-filters';
import { useFollowersSum } from '../hooks/use-followers-sum';
import { CreatorTableRow } from './creator-table-row';
import { SortableHeader } from './sortable-header';
import { TableSkeleton } from './table-skeleton';

function fLabel(sum: number | undefined): string {
  return sum != null ? `Seguidores (${formatFollowers(sum)})` : 'Seguidores';
}

function cLabel(total: number | undefined): string {
  return total != null
    ? `Creadores (${total.toLocaleString('es-ES')})`
    : 'Creadores';
}

function CreatorRows({
  creators,
  onSelect,
}: {
  creators: Creator[];
  onSelect: (id: string) => void;
}): ReactNode {
  return (
    <tbody>
      {creators.map((l) => (
        <CreatorTableRow
          key={l.id}
          creator={l}
          onClick={() => onSelect(l.id)}
        />
      ))}
    </tbody>
  );
}

type Props = {
  creators: Creator[];
  totalItems: number | undefined;
  onSelect: (id: string) => void;
  showSkeleton: boolean;
};

export function TableContent(p: Props): ReactNode {
  const { sortKey, sortDir, setSort } = useCreatorFilters();
  const { isAdmin } = useAuth();
  return (
    <table className="w-full min-w-[700px] text-left">
      <SortableHeader
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={setSort}
        creatorLabel={cLabel(p.totalItems)}
        followersLabel={fLabel(useFollowersSum())}
        isAdmin={isAdmin}
      />
      {p.showSkeleton ? (
        <TableSkeleton />
      ) : (
        <CreatorRows creators={p.creators} onSelect={p.onSelect} />
      )}
    </table>
  );
}
